// import { useAppState } from '@/src/store/ocg-app-state';

type ServiceName = 'segmentation' | 'analysis';

interface StatusResponse {
  status: 'ok' | 'busy' | 'error';
}

const intervals: Record<string, number> = {};
let lastActivityTime = Date.now();
const INACTIVITY_TIMEOUT_MS = 15 * 60 * 1000;
const LOG_OUT_TIMEOUT_MS = 30 * 60 * 1000;
let activityListenersAttached = false;
let activityPaused = false;

const updateActivity = () => {
  lastActivityTime = Date.now();
  if (activityPaused) {
    activityPaused = false;
    console.log('Activity detected. Resuming keep-alive requests.');
  }
};

const attachActivityListeners = () => {
  if (activityListenersAttached) return;
  window.addEventListener('mousemove', updateActivity);
  window.addEventListener('mousedown', updateActivity);
  window.addEventListener('keydown', updateActivity);
  window.addEventListener('touchstart', updateActivity);
  activityListenersAttached = true;
};

const detachActivityListeners = () => {
  if (Object.keys(intervals).length === 0 && activityListenersAttached) {
    window.removeEventListener('mousemove', updateActivity);
    window.removeEventListener('mousedown', updateActivity);
    window.removeEventListener('keydown', updateActivity);
    window.removeEventListener('touchstart', updateActivity);
    activityListenersAttached = false;
  }
};

const getUrl = (serviceName: ServiceName): string => {
  if (serviceName === 'segmentation') {
    return import.meta.env.VITE_OCG_BACKEND_SEGMENT_URL;
  }
  return import.meta.env.VITE_OCG_BACKEND_URL;
};

export async function getStatus(
  serviceName: ServiceName,
): Promise<StatusResponse> {
  const url = getUrl(serviceName);

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000);

  try {
    const response = await fetch(`${url}health_check`, {
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (response.ok) {
      try {
        const data = (await response.json()) as StatusResponse;
        console.log('Service status:', data);
        return data;
      }
      catch {
        console.log('Service status:', 'ok');
        return { status: 'ok' };
      }
    }
    else {
      console.log('Service status:', 'error');

      return { status: 'error' };
    }
  }
  catch (error) {
    clearTimeout(timeoutId);
    if (!(error instanceof Error)) throw error;
    if (error.name === 'AbortError') {
      console.log('Service status:', 'busy');

      return { status: 'busy' };
    }
    return { status: 'error' };
  }
}

export function endKeepAlive(serviceName: ServiceName) {
  if (intervals[serviceName]) {
    clearInterval(intervals[serviceName]);
    delete intervals[serviceName];
  }
  detachActivityListeners();
}

export function startKeepAlive(
  serviceName: ServiceName,
  interval: number = 30000,
) {
  if (intervals[serviceName]) {
    endKeepAlive(serviceName);
  }

  attachActivityListeners();
  updateActivity();
  void getStatus(serviceName);

  const id = window.setInterval(() => {
    if (Date.now() - lastActivityTime > INACTIVITY_TIMEOUT_MS) {
      activityPaused = true;
      if (Date.now() - lastActivityTime > LOG_OUT_TIMEOUT_MS) {
        endKeepAlive(serviceName);
        // useAppState().logOut(); // Uncomment this line to enable auto logout on innactivity
      }
      return;
    }
    if (activityPaused) return;

    void getStatus(serviceName);
  }, interval);

  intervals[serviceName] = id;
}
